/**
 * useStudentConfig hook
 * React Query hook for fetching student access mode configuration
 * Provides allowedMode, completedSessions with graceful error fallback
 */

import { useQuery } from '@tanstack/react-query';
import { getStudentConfig } from '@/services/studentConfig';
import type { StudentConfig, AllowedMode } from '@/types/studentConfig';

export interface UseStudentConfigReturn {
  /** Student's allowed session mode (defaults to 'serverless') */
  allowedMode: AllowedMode;
  /** Number of completed sessions (defaults to 0) */
  completedSessions: number;
  /** Loading state - true during initial fetch */
  isLoading: boolean;
  /** Error object if fetch failed */
  error: Error | null;
  /** Function to manually refetch */
  refetch: () => void;
}

export function useStudentConfig(): UseStudentConfigReturn {
  const { data, isLoading, error, refetch } = useQuery<StudentConfig, Error>({
    queryKey: ['student', 'config'],
    queryFn: () => getStudentConfig(),
    staleTime: 30000, // 30 seconds (same as usePebbles)
  });

  return {
    allowedMode: data?.allowedMode ?? 'serverless',
    completedSessions: data?.completedSessions ?? 0,
    isLoading,
    error,
    refetch: () => {
      refetch();
    },
  };
}
